import React from "react";
import { FileText, ArrowUpRight, Clock, BadgeCheck } from "lucide-react";
import { PROGRAMS } from "../../data/programs";

export const ProgramsSection = () => {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] text-aiou-gold font-semibold">
              Programs offered
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-aiou-ink leading-[1.1] tracking-tight">
              From Matric to Master's — pick the program that fits your goals.
            </h2>
            <p className="mt-5 text-aiou-slate text-base sm:text-lg leading-relaxed">
              All eight program categories accept international students for
              the Spring 2026 intake. Eligibility and total first-semester fees
              are listed below — the official prospectus for each program is
              linked too.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PROGRAMS.map((p, idx) => (
            <article
              key={p.id}
              data-testid={`program-card-${p.id}`}
              className="group relative rounded-2xl border border-aiou-sand bg-white p-7 hover:border-aiou-steel/30 hover:shadow-[0_16px_40px_rgba(36,66,101,0.08)] transition-all flex flex-col"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-mono text-aiou-mute">
                      0{idx + 1}
                    </span>
                    <span className="text-[11px] uppercase tracking-wider bg-aiou-steel/8 text-aiou-steel px-2 py-0.5 rounded-full">
                      {p.level}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl text-aiou-ink leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-aiou-slate italic leading-relaxed">
                    {p.tagline}
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-3 text-sm">
                <Row
                  icon={<BadgeCheck className="h-4 w-4" />}
                  label="Eligibility"
                  value={p.eligibility}
                />
                <Row
                  icon={<Clock className="h-4 w-4" />}
                  label="Duration"
                  value={p.duration}
                />
              </div>

              {p.highlights?.length > 0 && (
                <ul className="mt-5 space-y-1.5 text-sm text-aiou-slate">
                  {p.highlights.map((h) => (
                    <li
                      key={h}
                      className="flex gap-2 leading-relaxed before:content-['•'] before:text-aiou-gold before:font-bold before:mt-0"
                    >
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-aiou-sand">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-aiou-mute">
                    1st semester fee
                  </div>
                  <div className="font-serif text-2xl text-aiou-ink leading-none mt-1">
                    US$ {p.firstSemFee}
                  </div>
                  <div className="text-[11px] text-aiou-mute mt-1">
                    ({p.courses} courses · incl. registration & admission)
                  </div>
                </div>
                <a
                  href={p.prospectus}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`prospectus-${p.id}`}
                  className="inline-flex items-center gap-2 rounded-full border border-aiou-steel/20 bg-aiou-paper px-4 py-2 text-sm text-aiou-steel hover:bg-aiou-steel hover:text-white transition-colors"
                >
                  <FileText className="h-4 w-4" strokeWidth={1.7} />
                  Prospectus
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.8} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const Row = ({ icon, label, value }) => (
  <div className="flex gap-3">
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-aiou-gold/15 text-aiou-goldDark">
      {icon}
    </span>
    <div>
      <span className="text-[11px] uppercase tracking-wider text-aiou-mute block">
        {label}
      </span>
      <span className="text-aiou-ink">{value}</span>
    </div>
  </div>
);
